import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, Clock, Loader2, Download } from "lucide-react";
import { formatJamaicaDateTime as formatDateTime } from "@/lib/datetime";

export default function QuizzativeExportSync() {
  const { toast } = useToast();
  const [gradeLevel, setGradeLevel] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [fullSync, setFullSync] = useState(false);

  const statusQuery = useQuery<{
    configured: boolean;
    running: boolean;
    lastSync: any;
    lastExportedAt: string | null;
  }>({
    queryKey: ["/api/admin/quizzative-export/status"],
    refetchInterval: (query) => {
      return query.state.data?.running ? 2000 : 10000;
    },
  });

  const historyQuery = useQuery<any[]>({
    queryKey: ["/api/admin/quizzative-export/history"],
    refetchInterval: statusQuery.data?.running ? 2000 : false,
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const body: any = {};
      if (gradeLevel && gradeLevel !== "all") body.gradeLevel = gradeLevel;
      if (subject && subject !== "all") body.subject = subject;
      if (fullSync) body.fullSync = true;
      const res = await apiRequest("POST", "/api/admin/quizzative-export/sync", body);
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Sync started",
        description: "The sync is running in the background. Progress will update automatically.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative-export/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative-export/history"] });
    },
    onError: (err: any) => {
      toast({
        title: "Sync failed to start",
        description: err.message || "An error occurred",
        variant: "destructive",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative-export/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quizzative-export/history"] });
    },
  });

  const status = statusQuery.data;
  const latestRunning = historyQuery.data?.find((e: any) => e.status === "running");

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Question Bank Export</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Sync questions from the Quizzative Question Bank Export API
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Connection Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">API Key:</span>
            {status?.configured ? (
              <Badge variant="default">Configured</Badge>
            ) : (
              <Badge variant="destructive">Not Configured</Badge>
            )}
          </div>
          {status?.lastExportedAt && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Last export timestamp:</span>
              <span className="text-sm">{formatDateTime(status.lastExportedAt)}</span>
            </div>
          )}
          {status?.running && latestRunning && (
            <div className="space-y-2 border rounded-md p-3 bg-muted/30">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm font-medium">Sync in progress...</span>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <div>Total questions found: {latestRunning.totalQuestions || "..."}</div>
                <div className="flex gap-3 flex-wrap">
                  <span>Created: {latestRunning.questionsCreated || 0}</span>
                  <span>Updated: {latestRunning.questionsUpdated || 0}</span>
                  <span>Skipped: {latestRunning.questionsSkipped || 0}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Run Sync</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Grade</Label>
              <Select value={gradeLevel} onValueChange={setGradeLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="All grades" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All grades</SelectItem>
                  <SelectItem value="Grade 4">Grade 4</SelectItem>
                  <SelectItem value="Grade 5">Grade 5</SelectItem>
                  <SelectItem value="Grade 6">Grade 6</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Subject</Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger>
                  <SelectValue placeholder="All subjects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  <SelectItem value="Mathematics">Mathematics</SelectItem>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Science">Science</SelectItem>
                  <SelectItem value="Ability Test">Ability Test</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch id="full-sync" checked={fullSync} onCheckedChange={setFullSync} />
            <Label htmlFor="full-sync" className="text-sm">
              Full sync (ignore last timestamp, re-fetch all questions)
            </Label>
          </div>

          <Button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending || status?.running || !status?.configured}
          >
            {syncMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : status?.running ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {syncMutation.isPending ? "Starting..." : status?.running ? "Sync in Progress..." : "Start Sync"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sync History</CardTitle>
        </CardHeader>
        <CardContent>
          {historyQuery.isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">Loading...</span>
            </div>
          ) : !historyQuery.data?.length ? (
            <p className="text-sm text-muted-foreground">No syncs yet.</p>
          ) : (
            <div className="space-y-3">
              {historyQuery.data.map((entry: any) => (
                <div key={entry.id} className="flex items-start gap-3 border rounded-md p-3">
                  <div className="mt-0.5">
                    {entry.status === "completed" ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : entry.status === "failed" ? (
                      <XCircle className="h-4 w-4 text-red-600" />
                    ) : entry.status === "running" ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    ) : (
                      <Clock className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant={entry.status === "completed" ? "default" : entry.status === "failed" ? "destructive" : "secondary"}>
                        {entry.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(entry.startedAt)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground space-x-3 flex flex-wrap gap-1">
                      <span>Total: {entry.totalQuestions}</span>
                      <span>Created: {entry.questionsCreated}</span>
                      <span>Updated: {entry.questionsUpdated}</span>
                      <span>Skipped: {entry.questionsSkipped}</span>
                      {entry.objectivesLinked > 0 && <span>Objectives: {entry.objectivesLinked}</span>}
                    </div>
                    {entry.filters && (() => {
                      const f = typeof entry.filters === "string" ? JSON.parse(entry.filters) : entry.filters;
                      const parts: string[] = [];
                      if (f.gradeLevel) parts.push(f.gradeLevel);
                      if (f.subject) parts.push(f.subject);
                      if (f.strand) parts.push(f.strand);
                      if (f.fullSync) parts.push("full sync");
                      return parts.length > 0 ? (
                        <div className="text-xs text-muted-foreground">
                          Filters: {parts.join(", ")}
                        </div>
                      ) : null;
                    })()}
                    {entry.errors && (() => {
                      const errors = typeof entry.errors === "string" ? JSON.parse(entry.errors) : entry.errors;
                      if (!Array.isArray(errors) || errors.length === 0) return null;
                      const skipErrors = errors.filter((e: string) => e.startsWith("Skipped"));
                      const realErrors = errors.filter((e: string) => !e.startsWith("Skipped"));
                      return (
                        <div className="mt-1 space-y-1">
                          {realErrors.length > 0 && (
                            <div className="text-xs text-red-600">
                              {realErrors.slice(0, 5).map((e: string, i: number) => (
                                <div key={i}>{e}</div>
                              ))}
                              {realErrors.length > 5 && <div>...and {realErrors.length - 5} more errors</div>}
                            </div>
                          )}
                          {skipErrors.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {skipErrors.length} question(s) skipped (no matching grade)
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
